import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PartnerProvider } from './contexts/PartnerContext';
import { TourProvider } from './contexts/TourContext';
import { ToastProvider } from './contexts/ToastContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { StatusProvider } from './contexts/StatusContext';
import { DealProvider } from './contexts/DealContext';
import { ReminderProvider } from './contexts/ReminderContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CRMNavigationProvider } from './contexts/CRMNavigationContext';
import { LeadNotificationStack } from './components/shared/LeadNotificationStack';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import UpdateGate from './components/shared/UpdateGate';
import { UserRole } from './types/user.types';
import { EMAIL_ANALYTICS_ROLES } from './config/emailMarketingAccess';
// Import migration utility for temp document fix
import './utils/migrateTempDocs';

// ── Lazy-loaded route components (code splitting) ──────────────────────────
const Dashboard = lazy(() => import('./components/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const Escalations = lazy(() => import('./pages/escalations/Escalations'));
const BookingOutcomes = lazy(() => import('./pages/bookingOutcomes/BookingOutcomes'));
const ConsultationInsights = lazy(() => import('./pages/ConsultationInsights'));
const EmailReports = lazy(() => import('./pages/admin/EmailReports'));
const OutstandingBonds = lazy(() => import('./pages/admin/OutstandingBonds'));
const ProposalList = lazy(() => import('./pages/proposals/ProposalList'));
const ProposalBuilder = lazy(() => import('./pages/proposals/ProposalBuilder'));
const ProposalDetail = lazy(() => import('./pages/proposals/ProposalDetail'));
const ProposalAccessGate = lazy(() =>
  import('./components/proposals/ProposalAccessGate').then((m) => ({ default: m.ProposalAccessGate })),
);
const BonusDashboardPage = lazy(() => import('./pages/consultant/BonusDashboardPage').then(m => ({ default: m.BonusDashboardPage })));
const BondsBonusDashboard = lazy(() => import('./pages/bonds/BondsBonusDashboard').then(m => ({ default: m.BondsBonusDashboard })));
const BondsBonusAdminDashboard = lazy(() => import('./pages/bonds/BondsBonusAdminDashboard').then(m => ({ default: m.BondsBonusAdminDashboard })));
const ConsultantBonusAudit = lazy(() => import('./pages/admin/ConsultantBonusAudit').then(m => ({ default: m.ConsultantBonusAudit })));
const CallCentreBonusAudit = lazy(() => import('./pages/admin/CallCentreBonusAudit').then(m => ({ default: m.CallCentreBonusAudit })));
const DepositWaiverReport = lazy(() => import('./pages/reports/DepositWaiverReport'));
const ConsultantTransfer = lazy(() => import('./pages/admin/ConsultantTransfer').then(m => ({ default: m.ConsultantTransfer })));
const SecretsManagement = lazy(() => import('./pages/admin/SecretsManagement'));
const LeadDistribution = lazy(() => import('./pages/admin/LeadDistribution'));
const AriaMissionControl = lazy(() => import('./pages/admin/AriaMissionControl'));
const ScoreboardPage = lazy(() => import('./features/scoreboard/pages/ScoreboardPage'));
const AriaOrbit = lazy(() => import('./pages/admin/AriaOrbit'));
const MyCindyOrbit = lazy(() => import('./pages/MyCindyOrbit'));
const NotificationsAdmin = lazy(() => import('./pages/admin/NotificationsAdmin'));
const CindyTestForm = lazy(() => import('./pages/admin/CindyTestForm'));
const VoiceLab = lazy(() => import('./pages/admin/VoiceLab'));
const HeidiConsole = lazy(() => import('./pages/admin/HeidiConsole'));
const WendyConsole = lazy(() => import('./pages/admin/WendyConsole'));
const AriaTrainingConsole = lazy(() => import('./pages/admin/AriaTrainingConsole'));
const CindyLeadsConsole = lazy(() => import('./pages/admin/CindyLeadsConsole'));
const CindyAnalytics = lazy(() => import('./pages/admin/CindyAnalytics'));
const CareersApplicationsPage = lazy(() => import('./pages/admin/CareersApplicationsPage'));
const CapitalInvestmentPage = lazy(() => import('./pages/capital/CapitalInvestmentPage'));
const CapitalInvestmentAccessGate = lazy(() =>
  import('./components/capital/CapitalInvestmentAccessGate').then((m) => ({ default: m.CapitalInvestmentAccessGate })),
);
const CindyCostMonitor = lazy(() => import('./pages/admin/CindyCostMonitor'));
const LeadAssignmentAudit = lazy(() => import('./pages/admin/LeadAssignmentAudit').then(m => ({ default: m.LeadAssignmentAudit })));
const DispositionReconciliation = lazy(() => import('./pages/admin/DispositionReconciliation').then(m => ({ default: m.DispositionReconciliation })));
const ConsultantLeadReport = lazy(() => import('./pages/admin/ConsultantLeadReport'));
const ConsultantReviewAdmin = lazy(() => import('./pages/admin/ConsultantReviewAdmin').then(m => ({ default: m.ConsultantReviewAdmin })));
const ConsultantMonthlyReview = lazy(() => import('./pages/consultant/MonthlyReview').then(m => ({ default: m.MonthlyReview })));
const ExpiredReservationsPage = lazy(() => import('./pages/reservations/ExpiredReservationsPage').then(m => ({ default: m.ExpiredReservationsPage })));
const PerformanceHub = lazy(() => import('./pages/consultant/PerformanceHub').then(m => ({ default: m.PerformanceHub })));
const PerformanceHubDrilldown = lazy(() => import('./pages/consultant/PerformanceHubDrilldown').then(m => ({ default: m.PerformanceHubDrilldown })));
const UserList = lazy(() => import('./pages/users/UserList').then(m => ({ default: m.UserList })));
const CreateUser = lazy(() => import('./pages/users/CreateUser').then(m => ({ default: m.CreateUser })));
const EditUser = lazy(() => import('./pages/users/EditUser').then(m => ({ default: m.EditUser })));
const Onboarding = lazy(() => import('./pages/onboarding/Onboarding').then(m => ({ default: m.Onboarding })));
const DevelopmentList = lazy(() => import('./pages/developments/DevelopmentList').then(m => ({ default: m.DevelopmentList })));
const DevelopmentForm = lazy(() => import('./pages/developments/DevelopmentForm').then(m => ({ default: m.DevelopmentForm })));
const DevelopmentDetail = lazy(() => import('./pages/developments/DevelopmentDetail').then(m => ({ default: m.DevelopmentDetail })));
const DeleteDevelopment = lazy(() => import('./pages/developments/DeleteDevelopment').then(m => ({ default: m.DeleteDevelopment })));
const UnitList = lazy(() => import('./pages/units/UnitList').then(m => ({ default: m.UnitList })));
const UnitForm = lazy(() => import('./pages/units/UnitForm').then(m => ({ default: m.UnitForm })));
const UnitDetail = lazy(() => import('./pages/units/UnitDetail').then(m => ({ default: m.UnitDetail })));
const SystemCleanup = lazy(() => import('./pages/admin/SystemCleanup').then(m => ({ default: m.SystemCleanup })));
const UserImpersonation = lazy(() => import('./pages/admin/UserImpersonation'));
const InitializeDemoUsers = lazy(() => import('./pages/admin/InitializeDemoUsers'));
const PropCloudHQ = lazy(() => import('./pages/propcloud/PropCloudHQ').then(m => ({ default: m.PropCloudHQ })));
const SystemSettings = lazy(() => import('./pages/settings/SystemSettings').then(m => ({ default: m.SystemSettings })));
const PartnerSettings = lazy(() => import('./pages/settings/PartnerSettings').then(m => ({ default: m.PartnerSettings })));
const ActivityLog = lazy(() => import('./pages/admin/ActivityLog').then(m => ({ default: m.ActivityLog })));
const Activities = lazy(() => import('./pages/admin/Activities').then(m => ({ default: m.Activities })));
const UserProfile = lazy(() => import('./pages/profile/UserProfile').then(m => ({ default: m.UserProfile })));
const UserSettings = lazy(() => import('./pages/settings/UserSettings').then(m => ({ default: m.UserSettings })));
const PartnerHQ = lazy(() => import('./pages/partner/PartnerHQ').then(m => ({ default: m.PartnerHQ })));
const HQManagement = lazy(() => import('./pages/hq/HQManagement').then(m => ({ default: m.HQManagement })));
const CommissionSettings = lazy(() => import('./components/admin/CommissionSettings').then(m => ({ default: m.CommissionSettings })));
const CRMDashboard = lazy(() => import('./pages/crm/CRMDashboard').then(m => ({ default: m.CRMDashboard })));
const CRMv2Dashboard = lazy(() => import('./pages/crm-v2/CRMv2Dashboard').then(m => ({ default: m.CRMv2Dashboard })));
const RolesPermissions = lazy(() => import('./pages/admin/RolesPermissions').then(m => ({ default: m.RolesPermissions })));
const LeadStats = lazy(() => import('./components/crm/LeadStats').then(m => ({ default: m.LeadStats })));
const LeadAnalyticsPage = lazy(() => import('./pages/analytics/LeadAnalyticsPage').then(m => ({ default: m.LeadAnalyticsPage })));
const DeleteTestData = lazy(() => import('./components/admin/DeleteTestData').then(m => ({ default: m.DeleteTestData })));
const Training = lazy(() => import('./pages/training/Training').then(m => ({ default: m.Training })));
const FeedbackPage = lazy(() => import('./pages/feedback/FeedbackPage').then(m => ({ default: m.FeedbackPage })));
const SoftwareTrainingPage = lazy(() => import('./pages/training/SoftwareTrainingPage').then(m => ({ default: m.SoftwareTrainingPage })));
const TrainingManagementPage = lazy(() => import('./pages/admin/TrainingManagementPage').then(m => ({ default: m.TrainingManagementPage })));
const SupportHubPage = lazy(() => import('./pages/admin/SupportHubPage').then(m => ({ default: m.SupportHubPage })));
const MyTicketsPage = lazy(() => import('./pages/support/MyTicketsPage').then(m => ({ default: m.MyTicketsPage })));
const ContactDirectoryPage = lazy(() => import('./features/contactDirectory/ContactDirectoryPage').then(m => ({ default: m.ContactDirectoryPage })));
const BuyersListPage = lazy(() => import('./pages/admin/BuyersListPage').then(m => ({ default: m.BuyersListPage })));
const CustomAudiencesPage = lazy(() => import('./pages/admin/CustomAudiencesPage').then(m => ({ default: m.CustomAudiencesPage })));
const VipClientsPage = lazy(() => import('./pages/admin/VipClientsPage').then(m => ({ default: m.VipClientsPage })));
const Consultations = lazy(() => import('./pages/consultations/Consultations').then(m => ({ default: m.Consultations })));
const ViewingAgentDashboard = lazy(() => import('./pages/viewing/ViewingAgentDashboard'));
const ViewingOversightDashboard = lazy(() => import('./pages/viewing/ViewingOversightDashboard'));
const ViewingAvailabilityManager = lazy(() => import('./pages/viewing/ViewingAvailabilityManager'));
const ViewingDevelopmentDetail = lazy(() => import('./pages/viewing/ViewingDevelopmentDetail'));
const ViewingCalendarSetup = lazy(() => import('./components/viewing/ViewingCalendarSetup'));
const ViewingReport = lazy(() => import('./components/viewing/ViewingReport'));
const ViewingAgentBookings = lazy(() => import('./pages/viewing/ViewingAgentBookings'));
const ViewingEarnings = lazy(() => import('./pages/viewing/ViewingEarnings'));
const ViewingReportPublic = lazy(() => import('./components/viewing/ViewingReportPublic'));
const DocumentSignPublic = lazy(() => import('./pages/documents/DocumentSignPublic'));
const MyDocuments = lazy(() => import('./pages/documents/MyDocuments').then(m => ({ default: m.MyDocuments })));
const AccountingDashboard = lazy(() => import('./pages/accounting/AccountingDashboard').then(m => ({ default: m.AccountingDashboard })));
const GenerateInvoice = lazy(() => import('./pages/payments/GenerateInvoice').then(m => ({ default: m.GenerateInvoice })));
const AIAssistantPage = lazy(() => import('./pages/admin/AIAssistantPage').then(m => ({ default: m.AIAssistantPage })));
const NotificationSettingsPage = lazy(() => import('./pages/admin/NotificationSettingsPage').then(m => ({ default: m.NotificationSettingsPage })));
const ViewingSettingsPage = lazy(() => import('./pages/admin/ViewingSettingsPage').then(m => ({ default: m.ViewingSettingsPage })));
const ViewingWebhookTestingPage = lazy(() => import('./pages/admin/ViewingWebhookTestingPage'));
const MetaIntegrationTest = lazy(() => import('./pages/admin/MetaIntegrationTest').then(m => ({ default: m.MetaIntegrationTest })));
const MetaManualToken = lazy(() => import('./pages/admin/MetaManualToken').then(m => ({ default: m.MetaManualToken })));
const FacebookCallback = lazy(() => import('./pages/auth/FacebookCallback').then(m => ({ default: m.FacebookCallback })));
const AssignContacts = lazy(() => import('./components/admin/AssignContacts').then(m => ({ default: m.AssignContacts })));
const RoundRobinSettings = lazy(() => import('./components/admin/RoundRobinSettings').then(m => ({ default: m.RoundRobinSettings })));
const RoundRobinStatistics = lazy(() => import('./components/admin/RoundRobinStatistics').then(m => ({ default: m.RoundRobinStatistics })));
const ManualLeadCapture = lazy(() => import('./components/admin/ManualLeadCapture').then(m => ({ default: m.ManualLeadCapture })));
const UTMBuilder = lazy(() => import('./components/admin/UTMBuilder').then(m => ({ default: m.UTMBuilder })));
const LeadFormBuilder = lazy(() => import('./components/admin/LeadFormBuilder').then(m => ({ default: m.LeadFormBuilder })));
const LeadFormList = lazy(() => import('./components/admin/LeadFormList').then(m => ({ default: m.LeadFormList })));
const PublicLeadForm = lazy(() => import('./components/public/PublicLeadForm').then(m => ({ default: m.PublicLeadForm })));
const ImportContacts = lazy(() => import('./components/admin/ImportContacts').then(m => ({ default: m.ImportContacts })));
const OAuthConnectionManager = lazy(() => import('./components/admin/OAuthConnectionManager').then(m => ({ default: m.OAuthConnectionManager })));
const MetaOAuthCallback = lazy(() => import('./pages/oauth/MetaOAuthCallback').then(m => ({ default: m.MetaOAuthCallback })));
const GoogleOAuthCallback = lazy(() => import('./pages/oauth/GoogleOAuthCallback').then(m => ({ default: m.GoogleOAuthCallback })));
const SendGridCallback = lazy(() => import('./pages/oauth/SendGridCallback').then(m => ({ default: m.SendGridCallback })));
const WhatsAppOAuthCallback = lazy(() => import('./pages/oauth/WhatsAppOAuthCallback').then(m => ({ default: m.WhatsAppOAuthCallback })));
const EmailCampaignList = lazy(() => import('./components/admin/EmailCampaignList').then(m => ({ default: m.EmailCampaignList })));
const EmailCampaignCreate = lazy(() => import('./components/admin/EmailCampaignCreate').then(m => ({ default: m.EmailCampaignCreate })));
const EmailAnalyticsDashboard = lazy(() => import('./components/admin/EmailAnalyticsDashboard').then(m => ({ default: m.EmailAnalyticsDashboard })));
const EmailTemplateLibrary = lazy(() => import('./components/admin/EmailTemplateLibrary').then(m => ({ default: m.EmailTemplateLibrary })));
const EmailTemplateBuilder = lazy(() => import('./components/admin/EmailTemplateBuilder').then(m => ({ default: m.EmailTemplateBuilder })));
const EmailBuilder = lazy(() => import('./components/admin/email-builder').then(m => ({ default: m.EmailBuilder })));
const SpamScoreChecker = lazy(() => import('./components/admin/SpamScoreChecker').then(m => ({ default: m.SpamScoreChecker })));
const TestListManager = lazy(() => import('./components/admin/TestListManager').then(m => ({ default: m.TestListManager })));
const SMSCampaignList = lazy(() => import('./components/admin/SMSCampaignList').then(m => ({ default: m.SMSCampaignList })));
const SMSCampaignCreate = lazy(() => import('./components/admin/SMSCampaignCreate').then(m => ({ default: m.SMSCampaignCreate })));
const SMSAnalyticsDashboard = lazy(() => import('./components/admin/SMSAnalyticsDashboard').then(m => ({ default: m.SMSAnalyticsDashboard })));
const SMSReplyViewer = lazy(() => import('./components/admin/SMSReplyViewer').then(m => ({ default: m.SMSReplyViewer })));
const SMSCampaignDetail = lazy(() => import('./components/admin/SMSCampaignDetail').then(m => ({ default: m.SMSCampaignDetail })));
const WhatsAppTemplateList = lazy(() => import('./components/admin/WhatsAppTemplateList').then(m => ({ default: m.WhatsAppTemplateList })));
const WhatsAppTemplateBuilder = lazy(() => import('./components/admin/WhatsAppTemplateBuilder').then(m => ({ default: m.WhatsAppTemplateBuilder })));
const WhatsAppCampaignList = lazy(() => import('./components/admin/WhatsAppCampaignList').then(m => ({ default: m.WhatsAppCampaignList })));
const WhatsAppCampaignCreate = lazy(() => import('./components/admin/WhatsAppCampaignCreate').then(m => ({ default: m.WhatsAppCampaignCreate })));
const WhatsAppAnalyticsDashboard = lazy(() => import('./components/admin/WhatsAppAnalyticsDashboard').then(m => ({ default: m.WhatsAppAnalyticsDashboard })));
const WhatsAppBotAnalytics = lazy(() => import('./pages/whatsapp/WhatsAppBotAnalytics'));
const MarketingHub = lazy(() => import('./pages/marketing/MarketingHub').then(m => ({ default: m.MarketingHub })));
const AudiencesPage = lazy(() => import('./pages/marketing/AudiencesPage').then(m => ({ default: m.AudiencesPage })));
const FacebookFormsPage = lazy(() => import('./pages/marketing/FacebookFormsPage'));
const MarketingBuilder = lazy(() => import('./pages/marketing/MarketingBuilder').then(m => ({ default: m.MarketingBuilder })));
const WebhookManager = lazy(() => import('./components/admin/WebhookManager').then(m => ({ default: m.WebhookManager })));
const GraphicDesignManager = lazy(() => import('./pages/design/GraphicDesignManager').then(m => ({ default: m.GraphicDesignManager })));
const WorkflowList = lazy(() => import('./components/admin/WorkflowList').then(m => ({ default: m.WorkflowList })));
const WorkflowBuilder = lazy(() => import('./components/admin/WorkflowBuilder').then(m => ({ default: m.WorkflowBuilder })));
const WorkflowTester = lazy(() => import('./components/admin/WorkflowTester').then(m => ({ default: m.WorkflowTester })));
const LandingPageList = lazy(() => import('./components/admin/LandingPageList').then(m => ({ default: m.LandingPageList })));
const LandingPageBuilder = lazy(() => import('./components/admin/LandingPageBuilder').then(m => ({ default: m.LandingPageBuilder })));
const PublicLandingPage = lazy(() => import('./components/public/PublicLandingPage').then(m => ({ default: m.PublicLandingPage })));
const LeadScoringSettings = lazy(() => import('./pages/admin/LeadScoringSettings'));
const LeadAutomationSettings = lazy(() => import('./pages/admin/LeadAutomationSettings'));
const LeadAutomationLogs = lazy(() => import('./pages/admin/LeadAutomationLogs'));
const WhatsAppWebsiteLeads = lazy(() => import('./pages/admin/WhatsAppWebsiteLeads').then(m => ({ default: m.WhatsAppWebsiteLeads })));
const AITrainingSettings = lazy(() => import('./pages/admin/AITrainingSettings'));
const PropertyDetailPage = lazy(() => import('./pages/public/PropertyDetailPage'));
const PartnerPropertiesPage = lazy(() => import('./pages/public/PartnerPropertiesPage'));
const UnsubscribeSuccessPage = lazy(() => import('./pages/public/UnsubscribeSuccessPage'));
const UnsubscribeErrorPage = lazy(() => import('./pages/public/UnsubscribeErrorPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const EmailPreferencesPage = lazy(() => import('./pages/public/EmailPreferencesPage'));
const PublicWebsiteManager = lazy(() => import('./pages/admin/PublicWebsiteManager'));
const HQDashboard = lazy(() => import('./pages/admin/HQDashboard').then(m => ({ default: m.HQDashboard })));
const SystemRoundRobinOverview = lazy(() => import('./pages/admin/SystemRoundRobinOverview').then(m => ({ default: m.SystemRoundRobinOverview })));
const LeadAssignmentHistory = lazy(() => import('./pages/admin/LeadAssignmentHistory').then(m => ({ default: m.LeadAssignmentHistory })));
const PartnerManagement = lazy(() => import('./pages/admin/PartnerManagement').then(m => ({ default: m.PartnerManagement })));
const LegacyDealsPage = lazy(() => import('./pages/admin/LegacyDealsPage').then(m => ({ default: m.LegacyDealsPage })));
const CreditScoreSubmissions = lazy(() => import('./pages/admin/CreditScoreSubmissions').then(m => ({ default: m.CreditScoreSubmissions })));
const NurturingDashboard = lazy(() => import('./pages/admin/NurturingDashboard').then(m => ({ default: m.NurturingDashboard })));
const NurturingFormEmbed = lazy(() => import('./pages/admin/NurturingFormEmbed').then(m => ({ default: m.NurturingFormEmbed })));
const DuplicateManagement = lazy(() => import('./pages/admin/DuplicateManagement').then(m => ({ default: m.DuplicateManagement })));
const EuphoriaDialerSimulation = lazy(() => import('./pages/admin/EuphoriaDialerSimulation').then(m => ({ default: m.EuphoriaDialerSimulation })));
const CallCentreConfig = lazy(() => import('./pages/admin/CallCentreConfig'));
const CallCentreCalendarBooking = lazy(() => import('./pages/admin/CallCentreCalendarBooking'));
const CallCentreAgentDashboard = lazy(() => import('./pages/callCentre/CallCentreAgentDashboard'));
const AgentEmailFollowUps = lazy(() => import('./pages/callCentre/AgentEmailFollowUps'));
const CallCentreOverview = lazy(() => import('./pages/callCentre/CallCentreOverview'));
const SyndicationConfig = lazy(() => import('./pages/admin/SyndicationConfig').then(m => ({ default: m.SyndicationConfig })));
const WhatsAppFollowUpsConfig = lazy(() => import('./pages/admin/WhatsAppFollowUpsConfig'));
const WhatsAppBotBuilderList = lazy(() => import('./pages/admin/WhatsAppBotBuilder/ListPage'));
const WhatsAppBotBuilderEditor = lazy(() => import('./pages/admin/WhatsAppBotBuilder/EditorPage'));
const WhatsAppAutomationCenter = lazy(() => import('./pages/admin/WhatsAppAutomationCenter'));
const ReviewsAdmin = lazy(() => import('./pages/admin/ReviewsAdmin'));
const WebsiteCMSManager = lazy(() => import('./pages/admin/WebsiteCMSManager'));
const MobileAppCMSManager = lazy(() => import('./pages/admin/MobileAppCMSManager'));
const MediaManager = lazy(() => import('./pages/admin/MediaManager'));
const LeadIntelligenceDashboard = lazy(() => import('./pages/admin/LeadIntelligenceDashboard').then(m => ({ default: m.LeadIntelligenceDashboard })));
const StalledLeadsPage = lazy(() => import('./pages/admin/StalledLeadsPage').then(m => ({ default: m.StalledLeadsPage })));
const ActiveSalesPipelinePage = lazy(() => import('./pages/admin/ActiveSalesPipeline').then(m => ({ default: m.ActiveSalesPipelinePage })));
const OnboardingQuiz = lazy(() => import('./components/onboarding/OnboardingQuiz').then(m => ({ default: m.OnboardingQuiz })));
const ConsultantQuizResults = lazy(() => import('./components/admin/ConsultantQuizResults').then(m => ({ default: m.ConsultantQuizResults })));

// Initialize Sentry error monitoring
import { initSentry, setSentryUser } from './services/sentry';
initSentry();
import { LoginFormPasskey } from './components/auth/LoginFormPasskey';
import { PasswordReset } from './components/auth/PasswordReset';
import { Layout } from './components/shared/Layout';
// Biometric/Passkey UI hidden - backend code preserved for future re-enablement
// import { BiometricSettings } from './pages/settings/BiometricSettings';
// import { BiometricDebug } from './pages/admin/BiometricDebug';
// import { BiometricReset } from './pages/admin/BiometricReset';
// import { CleanupBiometric } from './components/admin/CleanupBiometric';
// import { AdminBiometricCleanup } from './components/admin/AdminBiometricCleanup';
// PERFORMANCE: Lazy load CRM components for code splitting
import { initializePermissionSystem } from './utils/initializePermissions';
import { fileManagementService } from './services/firebase/fileManagementService';
import { AIAssistant } from './components/ai/AIAssistant';
// Agent 2: Contact & Lead Management
// Agent 3: OAuth & Marketing Automation
// Agent 1: Email Marketing Platform
// Agent 1: SMS Marketing Platform
// Agent 3: WhatsApp Marketing Platform
// Marketing Automation Hub
// Webhook Integrations
// Graphic Design Manager
// Agent 3: Workflow Automation
// Agent 3: Landing Pages
// Agent 6: Lead Scoring Settings
// Lead Automation Settings
// Lead Automation Logs Dashboard
// AI Training Settings
// Agent 9: Public Property Pages
// Public Website Manager
// New HQ Admin Pages
// Legacy Deals (Old Deals) - Isolated feature for historical deal records
// Credit Score Submissions List
// Lead Nurturing Dashboard
// Duplicate Contact Management (Super Admin)
// Euphoria Dialer Simulation (Super Admin)
// Call Centre Configuration (HQ Management)
// PropData Syndication Configuration (Super Admin)
// Website CMS Manager (Super Admin)
// Mobile App CMS Manager (Super Admin)
// Media Manager (Super Admin)

/**
 * Legacy CRM contact deep-links (/crm/contacts/:id) now open the LIVE CRM.
 *
 * crm-v2 replaced the old CRM, but its contact URL is baked into thousands of
 * notification emails already sitting in inboxes, plus saved bookmarks. Rather
 * than leave those on a retired screen, redirect them — the query string is
 * carried through so ?tab=emails / ?tab=tasks still land on the right tab
 * (ContactDetailPage maps the legacy tab names).
 *
 * The old CRM's own People LIST at /crm/contacts is untouched, so the legacy
 * CRM shell still works for anything else it does.
 */
const LegacyContactRedirect: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const { search, hash } = window.location;
  return <Navigate to={`/crm-v2/contact/${contactId}${search}${hash}`} replace />;
};

/**
 * /admin/tickets → /admin/support-hub, preserving the query string — support
 * notification emails deep-link to /admin/tickets?item=<source>:<id>.
 */
const LegacyTicketsRedirect: React.FC = () => {
  const { search } = window.location;
  return <Navigate to={`/admin/support-hub${search}`} replace />;
};

// Wrapper component for classic EmailTemplateBuilder to support route params
const EmailTemplateBuilderWithParams: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  return <EmailTemplateBuilder templateId={templateId} />;
};

/**
 * ServiceInitializer - Initializes background services ONLY after user is FULLY authenticated.
 *
 * CRITICAL: This component exists because services like initializePermissionSystem
 * query Firestore collections that require authentication.
 * If these run before auth (e.g., on login page), they fail with "Missing or insufficient permissions".
 *
 * IMPORTANT: We check for `userProfile` not just `currentUser` because:
 * - `currentUser` is set immediately when Firebase onAuthStateChanged fires (even with stale/expired token)
 * - `userProfile` is only set after getUserByAuthUid succeeds (proving auth token is valid)
 * - This prevents services from starting when user has an expired cached session
 *
 * This component must be rendered INSIDE AuthProvider to access useAuth().
 */
const ServiceInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  const servicesInitialized = useRef(false);

  useEffect(() => {
    // Only initialize services once user is FULLY authenticated:
    // - loading is false (auth state resolved)
    // - currentUser exists (Firebase auth user)
    // - userProfile exists (Firestore profile loaded successfully - proves token is valid)
    if (!loading && currentUser && userProfile && !servicesInitialized.current) {
      servicesInitialized.current = true;
      console.log('ServiceInitializer: User fully authenticated, starting services...');
      // NOTE: reservation/unit expiry is handled EXCLUSIVELY by the scheduled
      // Cloud Functions (expirePreReservations / expireReservations). The old
      // browser-side timer loops were removed — a client-side expiry would
      // bypass the pre-deletion reservation snapshot.
      initializePermissionSystem();
      fileManagementService.initialize();
      // Set Sentry user context for error attribution
      setSentryUser({
        id: currentUser.uid,
        email: currentUser.email || undefined,
        role: userProfile.role,
      });
    }
  }, [currentUser, userProfile, loading]);

  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ServiceInitializer>
          <PartnerProvider>
            <TourProvider>
            <PermissionProvider>
              <NotificationProvider>
              <StatusProvider>
                <DealProvider>
                  <CRMNavigationProvider>
                  <ToastProvider>
                    <ReminderProvider>
                      <Router>
                  <Toaster position="top-right" toastOptions={{ duration: 4000 }} containerStyle={{ zIndex: 99999 }} />
                  <LeadNotificationStack />
                  {/* Applies pending live updates on navigation + "Update now" pill */}
                  <UpdateGate />
                  <Suspense fallback={<div style={{ background: '#0f172a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>}>
                  <Routes>
          <Route path="/login" element={<LoginFormPasskey />} />
          <Route path="/forgot-password" element={<PasswordReset />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/viewing/report/public/:token" element={<ViewingReportPublic />} />
          <Route path="/document/sign/:token" element={<DocumentSignPublic />} />
          <Route path="/auth/facebook/callback" element={<FacebookCallback />} />
          <Route path="/form/:formId" element={<PublicLeadForm />} />
          <Route path="/oauth/callback/meta" element={<MetaOAuthCallback />} />
          <Route path="/oauth/callback/google" element={<GoogleOAuthCallback />} />
          <Route path="/oauth/callback/sendgrid" element={<SendGridCallback />} />
          <Route path="/oauth/callback/whatsapp" element={<WhatsAppOAuthCallback />} />
          <Route path="/landing/:slug" element={<PublicLandingPage />} />
          <Route path="/property/:developmentId" element={<PropertyDetailPage />} />
          <Route path="/partner/:partnerId/properties" element={<PartnerPropertiesPage />} />
          <Route path="/unsubscribe-success" element={<UnsubscribeSuccessPage />} />
          <Route path="/unsubscribe-error" element={<UnsubscribeErrorPage />} />
          <Route path="/email-preferences" element={<EmailPreferencesPage />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="bonus-dashboard" element={<BonusDashboardPage />} />
            <Route path="bonds-bonus" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_BONDS, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <BondsBonusDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="bonds-bonus-admin" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <BondsBonusAdminDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="bonus-audit" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ConsultantBonusAudit />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="call-centre-bonus-audit" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <CallCentreBonusAudit />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="deposit-waiver-report" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <DepositWaiverReport />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="consultant-transfer" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ConsultantTransfer />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/secrets" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <SecretsManagement />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/lead-distribution" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <LeadDistribution />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/heidi" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <HeidiConsole />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/wendy" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <WendyConsole />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/voice-lab" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <VoiceLab />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/cindy-test-form" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <CindyTestForm />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/notifications" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <NotificationsAdmin />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/scoreboard" element={<Navigate to="/admin/scoreboard/cindy" replace />} />
            <Route path="admin/scoreboard/:board" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ScoreboardPage />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Old Cindy delivery-digest bell link (pre-fix notifications still carry it). */}
            <Route path="admin/aria/console" element={<Navigate to="/admin/aria-mission-control" replace />} />
            <Route path="admin/aria-mission-control" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <AriaMissionControl />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/aria-orbit" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <AriaOrbit />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Consultant's OWN Cindy orbit — server returns only leads they
                are the contact owner of; route roles are the UI gate. */}
            <Route path="my-cindy-orbit" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <MyCindyOrbit />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/aria-training" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <AriaTrainingConsole />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/cindy-leads" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <CindyLeadsConsole />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="careers-applications" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <CareersApplicationsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/cindy-analytics" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <CindyAnalytics />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/cindy-cost-monitor" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <CindyCostMonitor />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="lead-assignment-audit" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <LeadAssignmentAudit />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="disposition-reconciliation" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <DispositionReconciliation />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="consultant-lead-report" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ConsultantLeadReport />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="consultant-reviews" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ConsultantReviewAdmin />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="consultant/monthly-review" element={
              <ProtectedRoute requiredRoles={[UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ConsultantMonthlyReview />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="escalations" element={
              <ProtectedRoute requiredRoles={[UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN, UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <Escalations />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Email reports framework (2026-10-26) — super admins; the callables enforce it too. */}
            <Route path="admin/email-reports" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <EmailReports />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Outstanding Bonds dashboard (2026-10-27) — HQ management; the callable checks the same roles. */}
            <Route path="admin/outstanding-bonds" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <OutstandingBonds />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Weekly sales-meeting page — everyone sees every consultant (Jaco 2026-10-26). */}
            <Route path="consultation-insights" element={
              <ProtectedRoute>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <ConsultationInsights />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="booking-outcomes" element={
              <ProtectedRoute requiredRoles={[UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN, UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading…</div>}>
                  <BookingOutcomes />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="expired-reservations" element={
              <ProtectedRoute requiredRoles={[UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2, UserRole.HQ_MANAGER, UserRole.PROPCLOUDAI_SUPER_USER]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ExpiredReservationsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="consultant/performance-hub" element={
              <ProtectedRoute requiredRoles={[UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <PerformanceHub />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="consultant/performance-hub/:section" element={
              <ProtectedRoute requiredRoles={[UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <PerformanceHubDrilldown />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Capital Investment (Jaco 2026-10-12): the capital consultant + super users only. */}
            <Route path="capital-investment" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN, UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <CapitalInvestmentAccessGate>
                    <CapitalInvestmentPage />
                  </CapitalInvestmentAccessGate>
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="proposals" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN, UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ProposalAccessGate>
                    <ProposalList />
                  </ProposalAccessGate>
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="proposals/new" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN, UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ProposalAccessGate>
                    <ProposalBuilder />
                  </ProposalAccessGate>
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="proposals/all" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ProposalAccessGate>
                    <ProposalList />
                  </ProposalAccessGate>
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="proposals/:proposalId" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN, UserRole.HQ_CONSULTANT_LVL1, UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ProposalAccessGate>
                    <ProposalDetail />
                  </ProposalAccessGate>
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="propcloud-hq" element={<PropCloudHQ />} />
            <Route path="propcloud-partner" element={<PartnerHQ />} />
            <Route path="hq-management" element={<HQManagement />} />
            {/* Must be declared BEFORE crm/* so the specific path wins. */}
            <Route path="crm/contacts/:contactId" element={<LegacyContactRedirect />} />
            <Route path="crm/*" element={
              <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                <CRMDashboard />
              </Suspense>
            } />
            <Route path="crm-v2/*" element={
              <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                <CRMv2Dashboard />
              </Suspense>
            } />
            {/* Lead Analytics counts every contact: HQ management only (Jaco 2026-10-21) —
                the same roles that have the leadStats card. */}
            <Route path="propcloud/crm/lead-stats" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <LeadAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="analytics/leads" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <LeadAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="training" element={<Training />} />
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="consultations" element={<Consultations />} />
            <Route path="advertising" element={<MarketingHub />} />
            <Route path="marketing" element={<MarketingHub />} />
            <Route path="marketing/audiences" element={<AudiencesPage />} />
            <Route path="marketing/facebook-forms" element={<FacebookFormsPage />} />
            <Route path="marketing/ai-builder" element={<MarketingBuilder />} />
            <Route path="graphic-design" element={<GraphicDesignManager />} />
            <Route path="property-portals" element={<div>Property24 & Private Property - Coming Soon</div>} />
            <Route path="stats" element={<div>Statistics - Coming Soon</div>} />
            <Route path="developments" element={<DevelopmentList />} />
            <Route path="developments/new" element={<DevelopmentForm />} />
            <Route path="developments/:id" element={<DevelopmentDetail />} />
            <Route path="developments/:id/edit" element={<DevelopmentForm />} />
            <Route path="developments/delete" element={<DeleteDevelopment />} />
            <Route path="developments/:developmentId/units" element={<UnitList />} />
            <Route path="developments/:developmentId/units/new" element={<UnitForm />} />
            <Route path="developments/:developmentId/units/:unitId" element={<UnitDetail />} />
            <Route path="developments/:developmentId/units/:unitId/edit" element={<UnitForm />} />
            <Route path="users" element={<UserList />} />
            <Route path="users/new" element={<CreateUser />} />
            <Route path="users/:userId/edit" element={<EditUser />} />
            <Route path="admin/system-cleanup" element={<SystemCleanup />} />
            <Route path="admin/user-impersonation" element={<UserImpersonation />} />
            <Route path="admin/initialize-demo-users" element={<InitializeDemoUsers />} />
            <Route path="admin/tickets" element={<LegacyTicketsRedirect />} />
            <Route path="admin/support-hub" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <SupportHubPage />
              </ProtectedRoute>
            } />
            {/* Access follows Roles & Permissions (the All Contacts card), not a
                fixed role list: the page checks hasPermission and the directory
                callables check the same rule server-side (2026-09-30, hq_bonds). */}
            <Route path="admin/all-contacts" element={
              <ProtectedRoute>
                <ContactDirectoryPage />
              </ProtectedRoute>
            } />
            <Route path="admin/buyers-list" element={<BuyersListPage />} />
            <Route path="admin/custom-audiences" element={<CustomAudiencesPage />} />
            <Route path="admin/vip-clients" element={<VipClientsPage />} />
            <Route path="system-settings" element={<SystemSettings />} />
            <Route path="partner-settings" element={<PartnerSettings />} />
            <Route path="commission-settings" element={<CommissionSettings />} />
            <Route path="activities" element={<Activities />} />
            <Route path="activity-log" element={<ActivityLog />} />
            <Route path="partner-activity-log" element={<ActivityLog />} />
            <Route path="roles-permissions" element={<RolesPermissions />} />
            <Route path="reports" element={<div>Reports</div>} />
            <Route path="settings" element={<UserSettings />} />
            <Route path="profile" element={<UserProfile />} />
            <Route path="my-documents" element={<MyDocuments />} />
            <Route path="accounting" element={<AccountingDashboard />} />
            <Route path="payments/generate-invoice" element={<GenerateInvoice />} />

            {/* Viewing Agent Routes */}
            <Route path="viewing" element={<ViewingAgentDashboard />} />
            <Route path="viewing/calendar" element={<ViewingCalendarSetup />} />
            <Route path="viewing/availability" element={<ViewingAvailabilityManager />} />
            <Route path="viewing/development/:developmentId" element={<ViewingDevelopmentDetail />} />
            
            {/* Viewing Oversight Route */}
            <Route path="viewing-oversight" element={<ViewingOversightDashboard />} />
            <Route path="viewing/report/:bookingId" element={<ViewingReport />} />
            <Route path="viewing/bookings" element={<ViewingAgentBookings />} />
            <Route path="viewing/earnings" element={<ViewingEarnings />} />
            
            {/* Admin Viewing Routes */}
            <Route path="developments/:developmentId/calendar" element={<ViewingCalendarSetup />} />
            <Route path="my-tickets" element={<MyTicketsPage />} />
            {/* Biometric/Passkey routes hidden - backend code preserved for future re-enablement */}
            {/* <Route path="biometric-settings" element={<BiometricSettings />} /> */}
            {/* <Route path="admin/biometric-debug" element={<BiometricDebug />} /> */}
            {/* <Route path="admin/biometric-reset" element={<BiometricReset />} /> */}
            {/* <Route path="admin/biometric-cleanup" element={<CleanupBiometric />} /> */}
            {/* <Route path="admin/biometric-cleanup-admin" element={<AdminBiometricCleanup />} /> */}
            <Route path="admin/delete-test-data" element={<DeleteTestData />} />
            <Route path="admin/ai-assistant" element={<AIAssistantPage />} />
            <Route path="admin/ai-training" element={<AITrainingSettings />} />
            <Route path="notification-settings" element={<NotificationSettingsPage />} />
            <Route path="viewing-settings" element={<ViewingSettingsPage />} />
            <Route path="admin/viewing-webhooks" element={<ViewingWebhookTestingPage />} />
            <Route path="admin/meta-test" element={<MetaIntegrationTest />} />
            <Route path="admin/meta-token" element={<MetaManualToken />} />
            <Route path="admin/public-website" element={<PublicWebsiteManager />} />

            {/* Contact & Lead Management Routes (Agent 2) */}
            <Route path="admin/import-contacts" element={<ImportContacts />} />
            <Route path="admin/assign-contacts" element={<AssignContacts />} />
            <Route path="admin/capture-lead" element={<ManualLeadCapture />} />
            <Route path="admin/manual-lead-capture" element={<ManualLeadCapture />} />

            {/* HQ Admin Dashboard and Management */}
            <Route path="admin/dashboard" element={<HQDashboard />} />
            <Route path="admin/partners" element={<PartnerManagement />} />
            <Route path="admin/round-robin-overview" element={<SystemRoundRobinOverview />} />
            <Route path="admin/lead-assignment-history" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <LeadAssignmentHistory />
              </ProtectedRoute>
            } />
            <Route path="admin/credit-score-submissions" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <CreditScoreSubmissions />
              </ProtectedRoute>
            } />
            <Route path="admin/duplicates" element={<DuplicateManagement />} />
            <Route path="admin/dialer-simulation" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <EuphoriaDialerSimulation />
              </ProtectedRoute>
            } />
            <Route path="admin/call-centre-config" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <CallCentreConfig />
              </ProtectedRoute>
            } />
            <Route path="admin/call-centre-calendar-booking" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN, UserRole.HQ_CALL_CENTRE_AGENT]}>
                <CallCentreCalendarBooking />
              </ProtectedRoute>
            } />
            <Route path="admin/call-centre-overview" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <CallCentreOverview />
              </ProtectedRoute>
            } />
            {/* Call Centre Agent Dashboard */}
            <Route path="call-centre" element={
              <ProtectedRoute requiredRoles={[UserRole.HQ_CALL_CENTRE_AGENT, UserRole.PROPCLOUDAI_SUPER_USER]}>
                <CallCentreAgentDashboard />
              </ProtectedRoute>
            } />
            <Route path="call-centre/book" element={
              <ProtectedRoute requiredRoles={[UserRole.HQ_CALL_CENTRE_AGENT, UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <CallCentreCalendarBooking />
              </ProtectedRoute>
            } />
            <Route path="call-centre/agent/follow-ups" element={
              <ProtectedRoute requiredRoles={[UserRole.HQ_CALL_CENTRE_AGENT, UserRole.PROPCLOUDAI_SUPER_USER]}>
                <AgentEmailFollowUps />
              </ProtectedRoute>
            } />
            <Route path="admin/marketing-overview" element={<div className="p-8"><h1 className="text-2xl font-bold mb-4">Marketing Overview</h1><p>Unified multi-channel marketing analytics dashboard - Coming Soon</p></div>} />
            <Route path="admin/syndication" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <SyndicationConfig />
              </ProtectedRoute>
            } />
            <Route path="admin/whatsapp-follow-ups" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <WhatsAppFollowUpsConfig />
              </ProtectedRoute>
            } />
            <Route path="admin/whatsapp-bot-builder" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <WhatsAppBotBuilderList />
              </ProtectedRoute>
            } />
            <Route path="admin/whatsapp-bot-builder/:flowId" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <WhatsAppBotBuilderEditor />
              </ProtectedRoute>
            } />
            <Route path="admin/whatsapp-automation-center" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <WhatsAppAutomationCenter />
              </ProtectedRoute>
            } />
            <Route path="admin/reviews" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <ReviewsAdmin />
              </ProtectedRoute>
            } />
            <Route path="admin/website-cms" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <WebsiteCMSManager />
              </ProtectedRoute>
            } />
            <Route path="admin/mobile-app-cms" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <MobileAppCMSManager />
              </ProtectedRoute>
            } />
            <Route path="admin/media-manager" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <MediaManager />
              </ProtectedRoute>
            } />
            <Route path="admin/lead-intelligence" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <LeadIntelligenceDashboard />
              </ProtectedRoute>
            } />
            <Route path="admin/active-sales-pipeline" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <ActiveSalesPipelinePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="admin/lead-intelligence/stalled-leads" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <StalledLeadsPage />
              </ProtectedRoute>
            } />

            {/* Round Robin Routes (Agent 2) */}
            <Route path="admin/round-robin-settings" element={<RoundRobinSettings />} />
            <Route path="admin/round-robin-stats" element={<RoundRobinStatistics />} />

            {/* Lead Forms Routes (Agent 2) */}
            <Route path="admin/lead-forms" element={<LeadFormList />} />
            <Route path="admin/lead-forms/new" element={<LeadFormBuilder />} />
            <Route path="admin/lead-forms/:formId/edit" element={<LeadFormBuilder />} />

            {/* UTM Builder Route (Agent 2) */}
            <Route path="admin/utm-builder" element={<UTMBuilder />} />

            {/* OAuth Connections (Agent 3) */}
            <Route path="admin/oauth-connections" element={<OAuthConnectionManager />} />

            {/* Email Marketing Platform (Agent 1) */}
            <Route path="marketing/email/campaigns" element={<EmailCampaignList />} />
            <Route path="marketing/email/campaigns/new" element={<EmailCampaignCreate />} />
            <Route path="marketing/email/analytics" element={
              <ProtectedRoute requiredRoles={EMAIL_ANALYTICS_ROLES}>
                <EmailAnalyticsDashboard />
              </ProtectedRoute>
            } />
            <Route path="marketing/email/templates" element={<EmailTemplateLibrary />} />
            <Route path="marketing/email/templates/new" element={<EmailBuilder />} />
            <Route path="marketing/email/templates/:templateId/edit" element={<EmailBuilder />} />
            <Route path="marketing/email/templates/classic/new" element={<EmailTemplateBuilder />} />
            <Route path="marketing/email/templates/classic/:templateId/edit" element={<EmailTemplateBuilderWithParams />} />
            <Route path="marketing/email/spam-checker" element={<SpamScoreChecker />} />
            <Route path="marketing/email/test-lists" element={<TestListManager mode="email" />} />

            {/* SMS Marketing Platform (Agent 1) */}
            <Route path="marketing/sms/campaigns" element={<SMSCampaignList />} />
            <Route path="marketing/sms/campaigns/new" element={<SMSCampaignCreate />} />
            <Route path="marketing/sms/campaigns/:campaignId" element={<SMSCampaignDetail />} />
            <Route path="marketing/sms/analytics" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <SMSAnalyticsDashboard />
              </ProtectedRoute>
            } />
            <Route path="marketing/sms/replies" element={<SMSReplyViewer />} />
            <Route path="marketing/sms/test-lists" element={<TestListManager mode="sms" />} />

            {/* WhatsApp Marketing Platform (Agent 3) */}
            <Route path="marketing/whatsapp/templates" element={<WhatsAppTemplateList />} />
            <Route path="marketing/whatsapp/templates/new" element={<WhatsAppTemplateBuilder />} />
            <Route path="marketing/whatsapp/campaigns" element={<WhatsAppCampaignList />} />
            <Route path="marketing/whatsapp/campaigns/new" element={<WhatsAppCampaignCreate />} />
            <Route path="marketing/whatsapp/analytics" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <WhatsAppAnalyticsDashboard />
              </ProtectedRoute>
            } />
            <Route path="whatsapp/bot-analytics" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <WhatsAppBotAnalytics />
              </ProtectedRoute>
            } />

            {/* Workflow Automation (Agent 3) - Super Admin Only */}
            <Route path="marketing/workflows" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <WorkflowList />
              </ProtectedRoute>
            } />
            <Route path="marketing/workflows/new" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <WorkflowBuilder />
              </ProtectedRoute>
            } />
            <Route path="marketing/workflows/:id/edit" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <WorkflowBuilder />
              </ProtectedRoute>
            } />
            <Route path="marketing/workflows/:id/test" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <WorkflowTester />
              </ProtectedRoute>
            } />

            {/* Workflow Automation - Admin Aliases */}
            <Route path="admin/workflows" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <WorkflowList />
              </ProtectedRoute>
            } />
            <Route path="admin/workflow-tester" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <WorkflowTester />
              </ProtectedRoute>
            } />

            {/* Landing Pages (Agent 3) - Super Admin Only */}
            <Route path="marketing/landing-pages" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <LandingPageList />
              </ProtectedRoute>
            } />
            <Route path="marketing/landing-pages/new" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <LandingPageBuilder />
              </ProtectedRoute>
            } />
            <Route path="marketing/landing-pages/:id/edit" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <LandingPageBuilder />
              </ProtectedRoute>
            } />

            {/* Lead Scoring Settings (Agent 6) - Super Admin Only */}
            <Route path="admin/lead-scoring" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <LeadScoringSettings />
              </ProtectedRoute>
            } />

            {/* WhatsApp Website Lead Capture Settings */}
            <Route path="admin/whatsapp-website-leads" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <WhatsAppWebsiteLeads />
              </ProtectedRoute>
            } />

            {/* Lead Automation Settings - Super Admin Only */}
            <Route path="admin/lead-automation" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <LeadAutomationSettings />
              </ProtectedRoute>
            } />
            <Route path="admin/automation-logs" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <LeadAutomationLogs />
              </ProtectedRoute>
            } />

            {/* Lead Nurturing Dashboard - Super Admin Only */}
            <Route path="admin/nurturing" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <NurturingDashboard />
              </ProtectedRoute>
            } />
            <Route path="admin/nurturing/form-embed" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER]}>
                <NurturingFormEmbed />
              </ProtectedRoute>
            } />

            {/* Webhook Integrations - HQ Management */}
            <Route path="marketing/webhooks" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <WebhookManager />
              </ProtectedRoute>
            } />

            {/* Onboarding Quiz */}
            <Route path="onboarding-quiz" element={<OnboardingQuiz />} />
            {/* Software Training — consultant view */}
            <Route path="software-training" element={<SoftwareTrainingPage />} />

            {/* Training Management — super admin (Videos + Consultant Progress + Quiz Results) */}
            <Route path="admin/training-management" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <TrainingManagementPage />
              </ProtectedRoute>
            } />
            {/* Legacy redirect — old quiz-results URL still works */}
            <Route path="admin/consultant-quiz-results" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER]}>
                <TrainingManagementPage />
              </ProtectedRoute>
            } />

            {/* Legacy Deals (Old Deals) - Super Admin Only */}
            <Route path="admin/legacy-deals" element={
              <ProtectedRoute requiredRoles={[UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.HQ_ADMIN]}>
                <LegacyDealsPage />
              </ProtectedRoute>
            } />
            {/* Unknown in-app paths: show a 404 instead of silently redirecting. Nested under the
                protected layout so signed-out users are still sent to /login first. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          
          <Route path="/unauthorized" element={<div>Unauthorized</div>} />
                  </Routes>
                  </Suspense>
                    </Router>
                  </ReminderProvider>
                </ToastProvider>
                  </CRMNavigationProvider>
              </DealProvider>
            </StatusProvider>
              </NotificationProvider>
            </PermissionProvider>
            </TourProvider>
          </PartnerProvider>
        </ServiceInitializer>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;